import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import styles from './RechargeQRPage.module.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import { createSystemLoginApiClient } from '../utils/systemLoginApi';
import config from '../config';
import { fetchWithAuth } from '../utils/api';
import wechatIcon from '../assets/wechat-icon.png';
import alipayIcon from '../assets/alipay-icon.webp';

type PaymentMethod = 'wechat' | 'alipay' | 'unknown';

const WechatIcon = () => (
  <div className={styles.paymentIcon}>
    <img src={wechatIcon} alt="WeChat Pay" width="40" height="40" />
  </div>
);

const AlipayIcon = () => (
  <div className={styles.paymentIcon}>
    <img src={alipayIcon} alt="Alipay" width="40" height="40" />
  </div>
);

const RechargeQRPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const systemLogin = useSystemLogin();
  const apiClient = createSystemLoginApiClient(systemLogin);
  
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [qrCode, setQrCode] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('unknown');
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('CNY');
  const checkStatusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qrCodeParam = params.get('qrCode');
    const orderIdParam = params.get('orderId');
    const deadlineMillsParam = params.get('deadlineMills');
    const codeParam = params.get('code');
    const paymentMethodParam = params.get('paymentMethod') as PaymentMethod;
    const amountParam = params.get('amount');
    const currencyParam = params.get('currency');

    if (qrCodeParam) {
      setQrCode(decodeURIComponent(qrCodeParam));
    } else {
      setError('缺少二维码信息');
    }

    if (orderIdParam) {
      setOrderId(orderIdParam);
    }

    if (codeParam) {
      setCode(codeParam);
    }

    if (paymentMethodParam) {
      setPaymentMethod(paymentMethodParam);
    }

    if (amountParam) {
      setAmount(amountParam);
    }

    if (currencyParam) {
      setCurrency(currencyParam);
    }

    if (deadlineMillsParam) {
      const deadlineMillis = parseInt(deadlineMillsParam, 10);
      const now = Date.now();
      const remainingTime = Math.max(0, Math.floor((deadlineMillis - now) / 1000));
      setTimeLeft(remainingTime);
    }

    startTimer();
    startPaymentStatusCheck();

    return () => {
      if (checkStatusIntervalRef.current) {
        clearInterval(checkStatusIntervalRef.current);
        checkStatusIntervalRef.current = null;
      }
    };
  }, [location.search]);

  useEffect(() => {
    if (timeLeft <= 0) {
      if (checkStatusIntervalRef.current) {
        clearInterval(checkStatusIntervalRef.current);
        checkStatusIntervalRef.current = null;
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    // 当 orderId 设置完成后，确保启动支付状态轮询
    if (orderId) {
      if (checkStatusIntervalRef.current) {
        clearInterval(checkStatusIntervalRef.current);
        checkStatusIntervalRef.current = null;
      }
      checkStatusIntervalRef.current = setInterval(() => {
        checkPaymentStatus(orderId);
      }, 1000);
    }
  }, [orderId]);

  const checkPaymentStatus = async (orderId: string) => {
    try {
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/aggregatePay/queryPayStatus?orderId=${orderId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'en,zh-CN;q=0.9,zh;q=0.8',
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`,
            'appIntVersion': '11',
            'appversion': '0.7.0',
            'cityId': '1534422005300989952',
            'language': 'CN',
            'languageType': 'CN',
            'lat': '1.323633',
            'lon': '103.800345',
            'orderToken': code,
            'os': 'ios',
          }
        },
        navigate
      );

      if (data.code === 1 && data.data !== undefined) {
        const status = Number(data.data);
        
        switch (status) {
          case 0:
          case 3:
            console.log('支付进行中，继续查询...');
            break;
          case 1:
            if (checkStatusIntervalRef.current) {
              clearInterval(checkStatusIntervalRef.current);
              checkStatusIntervalRef.current = null;
            }
            navigate(`/payment-failed?orderId=${orderId}`);
            break;
          case 2:
            if (checkStatusIntervalRef.current) {
              clearInterval(checkStatusIntervalRef.current);
              checkStatusIntervalRef.current = null;
            }
            navigate(`/payment-timeout?orderId=${orderId}`);
            break;
          case 4:
            if (checkStatusIntervalRef.current) {
              clearInterval(checkStatusIntervalRef.current);
              checkStatusIntervalRef.current = null;
            }
            navigate(`/payment-success?orderId=${orderId}`);
            break;
          default:
            console.error('未知的支付状态:', status);
            break;
        }
      } else {
        console.error('查询支付状态未成功:', data.code, data.msg);
        console.log('将在下次轮询时重试...');
      }
    } catch (error) {
      console.error('检查支付状态时出错:', error);
      console.log('将在下次轮询时重试...');
    }
  };

  const startTimer = () => {
    if (timeLeft > 0 && orderId) {
      startPaymentStatusCheck();
    }
  };

  const startPaymentStatusCheck = () => {
    if (orderId) {
      if (checkStatusIntervalRef.current) {
        clearInterval(checkStatusIntervalRef.current);
        checkStatusIntervalRef.current = null;
      }

      checkStatusIntervalRef.current = setInterval(() => {
        checkPaymentStatus(orderId);
      }, 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (checkStatusIntervalRef.current) {
        clearInterval(checkStatusIntervalRef.current);
        checkStatusIntervalRef.current = null;
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleBack = () => {
    navigate('/recharge');
  };

  // 获取币种符号
  const getCurrencySymbol = (currencyCode: string): string => {
    const currencySymbols: { [key: string]: string } = {
      'CNY': '¥',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'KRW': '₩',
      'HKD': 'HK$',
      'SGD': 'S$',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'RUB': '₽',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': '$',
      'ZAR': 'R',
      'TRY': '₺',
      'THB': '฿',
      'MYR': 'RM',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫'
    };
    
    return currencySymbols[currencyCode] || currencyCode;
  };

  const getPaymentInstructions = () => {
    switch (paymentMethod) {
      case 'wechat':
        return (
          <>
            <div className={styles.qrInstructions}>
              请使用微信扫描下方二维码完成支付
            </div>
            <WechatIcon />
          </>
        );
      case 'alipay':
        return (
          <>
            <div className={styles.qrInstructions}>
              请使用支付宝扫描下方二维码完成支付
            </div>
            <AlipayIcon />
          </>
        );
      default:
        return (
          <div className={styles.qrInstructions}>
            请扫描下方二维码完成支付
          </div>
        );
    }
  };

  if (!systemLogin.isLoggedIn) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.content}>
          <div className={styles.loginPrompt}>
            <h2>请先登录</h2>
            <p>登录后即可进行支付</p>
            <button onClick={() => navigate('/login')} className={styles.loginButton}>
              前往登录
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.backIcon}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            返回
          </button>
          <h1 className={styles.title}>扫码支付</h1>
        </header>

        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
            <span>加载中...</span>
          </div>
        )}

        <main className={styles.main}>
          {error ? (
            <div className={styles.errorContainer}>
              <div className={styles.errorMessage}>{error}</div>
              <button className={styles.retryButton} onClick={() => navigate(-1)}>
                返回重试
              </button>
            </div>
          ) : (
            <div className={styles.paymentContent}>
              {getPaymentInstructions()}

              <div className={styles.qrCodeWrapper}>
                {qrCode && (
                  <div className={styles.qrCode}>
                    <QRCodeSVG
                      value={qrCode}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                )}
              </div>

              <div className={styles.amount}>
                <span className={styles.currencySymbol}>{getCurrencySymbol(currency)}</span>
                <span>{amount}</span>
              </div>

              <div className={styles.timer}>
                {timeLeft > 0 ? (
                  <>
                    <span>二维码有效时间：</span>
                    <span className={styles.countdown}>{formatTime(timeLeft)}</span>
                  </>
                ) : (
                  <span className={styles.expired}>二维码已过期</span>
                )}
              </div>

              <div className={styles.orderInfo}>
                <span>订单号：</span>
                <span className={styles.orderId}>{orderId}</span>
              </div>

              <div className={styles.note}>
                支付完成后页面会自动跳转，请勿关闭此页面
              </div>
            </div>
          )}
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default RechargeQRPage;